import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { callApi } from "../../lib/utils"; // Assuming you have a utility for API calls
import { Link } from 'react-router-dom';
import moment from 'moment';
import { styles } from "./style";
import { css } from "aphrodite";
import CustomLoader from "../GenericComponents/CustomLoader";
import Footer from "../GenericComponents/Footer";
import "../AccountPage.css";

const ManageSubscription = ({ user }) => {
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [previousSubscriptions, setPreviousSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const isReadOnly = user?.roleId === 10;

    useEffect(() => {
        const fetchSubscriptions = () => {
            setLoading(true);
            setError('');

            // --- REAL API CALL ---
            callApi("/api/patient/subscription", {}, "GET")
                .then(response => {
                    if (response.data && response.data.success) {
                        const data = response.data.data;

                        // const getStatusText = (status) => {
                        //     switch (status) {
                        //         case 1: return 'active';
                        //         case 4: return 'canceled';
                        //         default: return 'inactive';
                        //     }
                        // };

                        // Map the API response to the component's state structure
                        if (data.patientSubscription) {
                            const ps = data.patientSubscription;
                            const plan = ps.subscriptionPlan;
                            const tier = ps.subscriptionTier;

                            const formattedCurrentSubscription = {
                                id: ps.id,
                                planName: `${tier.tierName} - ${plan.paymentFrequency.frequency.charAt(0) + plan.paymentFrequency.frequency.slice(1).toLowerCase()}`,
                                status: ps.status === 1 || ps.status === 5 ? 'active' : 'cancelled', // Translate status
                                startDate: ps.startDate,
                                renewalDate: ps.nextDueDate,
                                canceledAt: ps.canceledAt, // Add canceledAt date
                                amount: plan.price,
                                currency: plan.currency
                            };
                            setCurrentSubscription(formattedCurrentSubscription);
                        } else {
                            // Handle case where there is no active subscription
                            setCurrentSubscription(null);
                        }

                        // Map the previousSubscription array from the API
                        setPreviousSubscriptions(data.previousSubscription?.map(sub => {
                            const plan = sub.subscriptionPlan;
                            const tier = sub.subscriptionTier;
                            const tierName = tier.tierName;
                            const frequency = plan.paymentFrequency.frequency.charAt(0) + plan.paymentFrequency.frequency.slice(1).toLowerCase();

                            return {
                                id: sub.id,
                                planName: `${tierName} ${frequency}`,
                                status: plan.status === 1 || plan.status === 5 ? 'active' : 'cancelled', // Translate status
                                startDate: sub.startDate,
                                endDate: sub.canceledAt || sub.endDate, // Prefer canceledAt for the end date
                                amount: plan.price,
                                cancellationReason: sub.cancellationComment || 'N/A', // Add cancellation reason
                            };
                        }) || []);
                    } else {
                        setError('Could not retrieve subscription details.');
                    }
                })
                .catch(err => {
                    setError('An error occurred while fetching your subscriptions.');
                    console.error("Fetch subscription error:", err);
                })
                .finally(() => {
                    setLoading(false);
                });
        };

        fetchSubscriptions();
    }, []);

    const StatusBadge = ({ status }) => {
        const statusClass = status === 'active' ? 'bg-success text-white' : 'bg-danger text-white';
        const iconClass = status === 'active' ? 'bi-check-circle-fill' : 'bi-x-circle-fill';
        return (
            <span className={`badge rounded-pill fs-6 ${statusClass}`}>
                <i className={`bi ${iconClass} me-2`}></i>
                <span className="text-capitalize">{status}</span>
            </span>
        );
    };

    const SubscriptionInfo = ({ icon, label, value }) => (
        <div className="subscription-info-row">
            <span className="subscription-info-label text-muted small">
                <i className={`bi ${icon} me-2`}></i>
                {label}:
            </span>
            <span className="subscription-info-value fw-bold">{value}</span>
        </div>
    );

    return (
        <div className="account-page">
            <CustomLoader loading={loading} />
            <div className={css(styles.mainGrid)}>
                <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet" />
                <div className={css(styles.header)}>{"Subscriptions"}</div>

                {/* --- CURRENT SUBSCRIPTION CARD --- */}
                {
                    loading ? (
                        // <div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        <></>
                    ) :
                        error ? (
                            <div className="alert alert-danger">{error}</div>
                        ) : currentSubscription ? (
                            <div className="subscription-card">
                                <div className="subscription-card-header">
                                    <div className="subscription-card-title-row">
                                        <div className="current-plan-label text-primary">CURRENT PLAN</div>
                                        <div className="subscription-plan-name-row">
                                            <h2 className="h4 mb-0 fw-bold">{currentSubscription.planName}</h2>
                                            <StatusBadge status={currentSubscription.status} />
                                        </div>
                                    </div>
                                    <div className="subscription-card-header-actions">
                                        {!isReadOnly && currentSubscription.status === 'active' && (
                                            <Link
                                                to={{
                                                    pathname: "/faqs",
                                                    state: { openCancellationTab: true }
                                                }}
                                                className="text-danger cancel-icon-link"
                                                title="Request for Subscription Cancellation">
                                                <i className="bi bi-calendar2-x fs-5"></i>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                                <hr className="my-4" />
                                <div className="row subscription-details-row g-3">
                                    <div className="col-12 col-md-4">
                                        <SubscriptionInfo
                                            icon="bi-shield-check"
                                            label="Plan Status"
                                            value={currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1)}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        {currentSubscription.status === 'active' ? (
                                            <SubscriptionInfo
                                                icon="bi-calendar-event"
                                                label="Renews On"
                                                value={moment(currentSubscription.renewalDate).format('MM/DD/YYYY')}
                                            />
                                        ) : (
                                            <SubscriptionInfo
                                                icon="bi-calendar-x"
                                                label="Canceled At"
                                                value={moment(currentSubscription.canceledAt).format('MM/DD/YYYY')}
                                            />
                                        )}
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <SubscriptionInfo
                                            icon="bi-calendar-check"
                                            label="Started On"
                                            value={moment(currentSubscription.startDate).format('MM/DD/YYYY')}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center p-4 card shadow-sm">
                                <p>You do not have an active subscription.</p>
                                {/* <Link to="/new/order" className="btn btn-primary">Subscribe Now</Link> */}
                            </div>
                        )}

                {/* --- PREVIOUS SUBSCRIPTIONS LIST --- */}
                {
                    // loading ? (
                    //     <p>Loading history...</p>
                    // ) : 
                    previousSubscriptions.length > 0 && (
                            <div className='mt-5'>
                            <h4 className="mb-3">Past Subscriptions</h4>
                            <div className="card">
                                <div className="table-responsive past-subscriptions-table-wrapper">
                                    <table className="table table-hover align-middle mb-0 past-subscriptions-table">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col" className="ps-3">Plan</th>
                                                <th scope="col">Status</th>
                                                <th scope="col">Started On</th>
                                                <th scope="col">Canceled At</th>
                                                <th scope="col">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previousSubscriptions.map(sub => (
                                                <tr key={sub.id}>
                                                    <td className="ps-3">
                                                        <div className="fw-bold">{sub.planName}</div>
                                                        <small className="text-muted">${sub.amount.toFixed(2)}</small>
                                                    </td>
                                                    <td><StatusBadge status={sub.status} /></td>
                                                    <td>{moment(sub.startDate).format('MM/DD/YYYY')}</td>
                                                    <td>{moment(sub.endDate).format('MM/DD/YYYY')}</td>
                                                    <td>{sub.cancellationReason}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
            <Footer />
        </div>
    );
};

export default connect(state => ({
    user: state.user
}))(ManageSubscription);

